import { Fragment, useId, useMemo, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo } from '@/componentes/Campo';
import { CampoDeCep, type EnderecoDoCep } from '@/componentes/CampoDeCep';
import { CamposDoEndereco } from '@/componentes/CamposDoEndereco';
import { ControleCarregando } from '@/componentes/Esqueleto';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { BotaoDoRaro } from '@/componentes/LinhaDoPreenchido';
import { ehCampoDoEndereco, enderecoDe } from '@/componentes/resumo-do-endereco';
import { Sobreposicao } from '@/componentes/JanelaDeAcao';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Medida } from '@/componentes/Medida';
import { Seletor } from '@/componentes/Seletor';
import { RodapeTabela, Tabela, type Coluna } from '@/componentes/Tabela';
import { usePaginacao } from '@/modulos/operacao/paginacao';
import { formatarInteiro, interpretarNumero } from '@/lib/formato';
import { comVersao } from '@/lib/versao-da-ficha';
import { useSessao } from '@/modulos/sessao/consultas';

import { caixasDaLinha, caixasDoEnvio } from './caixas-do-cadastro';
import { comErroDe, conferirTodos, type ErrosDeCampo, marcaDeErro, NENHUM_ERRO, temErro } from './erros-de-campo';
import {
  explicarFalha,
  useCadastros,
  useGravarCadastro,
  usePostosCadastrados,
  useProdutosCadastrados,
  useTanquesCadastrados,
} from './consultas';
import type { PostoCadastradoDaApi, ProdutoCadastradoDaApi, TanqueCadastradoDaApi } from './api';
import { HubDoCombustivel } from './HubDoCombustivel';
import { categoriaPeloNome, unidadePelaCategoria } from './produto-pelo-nome';

/**
 * Tanque, posto e produto — o que precisa existir ANTES do primeiro
 * abastecimento.
 *
 * ## Por que esta tela passou a existir
 *
 * Até aqui os três só entravam por SQL. A consequência era dura e estava
 * escrita: com o banco vazio, o módulo 08 não tinha o que mostrar e o
 * formulário de lançamento não tinha o que oferecer. O estado vazio de
 * `/combustivel/tanques` dizia, honestamente, que tanque entra pelo banco e que
 * era preciso pedir a quem administra — honesto, e ainda assim um sistema pela
 * metade.
 *
 * A decisão 6 do dono fechou a dúvida: *"todas as situações podem acontecer,
 * devem ser parametrizável no sistema"*. Tanque fixo **e** comboio, os dois, e
 * parametrizável quer dizer **por aqui**, não por script.
 *
 * ## Três listas, uma tela
 *
 * Três entradas no menu lateral afogariam o mapa da solução, e nenhuma das três
 * é um módulo: são listas do mesmo assunto. O seletor é o mesmo do módulo 01,
 * pela mesma razão, e por isso ele mora em `componentes/`.
 *
 * ## Uma única ação laranja
 *
 * Uma só, e ela TROCA conforme o momento: com a lista à mostra, a ação é
 * "Cadastrar"; com o formulário aberto, é "Gravar", e o cabeçalho passa a
 * oferecer a saída em contorno. As duas nunca aparecem juntas — duas ações
 * primárias é o mesmo que nenhuma. Escolher a lista é navegação, não ação, e
 * por isso o seletor usa contorno da marca, e não preenchimento.
 *
 * ## Não há excluir, e isso é decisão
 *
 * O backend não tem rota de `DELETE`, e não é esquecimento: apagar um tanque
 * levaria junto o livro dele — todo recebimento, toda saída, toda medição. O
 * que existe é **inativar**, que preserva a história. Um cadastro inativo some
 * das listas de escolha das outras telas e continua explicando os lançamentos
 * que já aconteceram.
 */

type Assunto = 'tanques' | 'postos' | 'produtos';

const ASSUNTOS: readonly { readonly valor: Assunto; readonly rotulo: string }[] = [
  { valor: 'tanques', rotulo: 'Tanques' },
  { valor: 'postos', rotulo: 'Postos' },
  { valor: 'produtos', rotulo: 'Produtos' },
];

/** O nome de cada assunto no singular e no plural, para as frases da tela. */
const NOME: Record<Assunto, { readonly um: string; readonly muitos: string }> = {
  tanques: { um: 'tanque', muitos: 'Tanques' },
  postos: { um: 'posto', muitos: 'Postos' },
  produtos: { um: 'produto', muitos: 'Produtos' },
};

/* As grafias oficiais. `combustivel` é o valor que o banco guarda; "Combustível"
   é o que a pessoa lê. O mapa existe para a tela nunca escrever o valor cru — o
   mesmo motivo pelo qual nome de tabela não aparece em tela nenhuma. */
const CATEGORIA: Record<string, string> = {
  combustivel: 'Combustível',
  lubrificante: 'Lubrificante',
  arla: 'ARLA',
  outro: 'Outro',
};

const TIPO_DE_TANQUE: Record<string, string> = { fixo: 'Fixo', comboio: 'Comboio' };

/** Situação escrita, nunca só a cor — e com a grafia do manual. */
function Situacao({ ativo }: { readonly ativo: boolean }) {
  return <span className="txt-corpo text-tinta-secundaria">{ativo ? 'Disponível' : 'Arquivado'}</span>;
}

function Codigo({ valor }: { readonly valor: string }) {
  return <span className="codigo">{valor}</span>;
}

/* ==========================================================================
 * O formulário
 * ======================================================================= */

/** O que um campo do formulário precisa saber sobre si. */
interface CampoDoFormulario {
  readonly nome: string;
  readonly rotulo: string;
  /* `cep`, `uf` e `municipio`: o endereco do posto, o mesmo da filial e da
     obra — CEP que busca sozinho, UF da lista e municipio do IBGE. */
  /* `sistema` (0131, decisao 57): o codigo, que o sistema cria ao salvar e
     ninguem digita. So para ler, e nunca vai no envio. */
  /* `fornecedor` (31/10/2026): a empresa dona do posto, da lista de
     fornecedores. E por ela que a pessoa de posto parceiro alcanca o posto. */
  readonly tipo: 'texto' | 'numero' | 'escolha' | 'referencia' | 'fornecedor' | 'cep' | 'uf' | 'municipio' | 'sistema';
  readonly opcional?: boolean;
  readonly ajuda?: string;
  readonly opcoes?: readonly { readonly valor: string; readonly rotulo: string }[];
  readonly placeholder?: string;
  /** Nome e descrição ocupam duas colunas da grade (R01, largura pelo dado). */
  readonly largo?: boolean;
  /**
   * O campo raro, que fica atrás de "Mais dados" (31/10/2026, o desenho do
   * lançamento de abastecimento): as tolerâncias do tanque, que quase ninguém
   * muda depois do cadastro.
   */
  readonly raro?: boolean;
}

const CAMPOS: Record<Assunto, readonly CampoDoFormulario[]> = {
  produtos: [
    { nome: 'codigo', rotulo: 'Código', tipo: 'sistema' },
    { nome: 'nome', rotulo: 'Nome', tipo: 'texto', placeholder: 'Óleo diesel S10', largo: true },
    {
      nome: 'categoria',
      rotulo: 'Categoria',
      tipo: 'escolha',
      opcoes: Object.entries(CATEGORIA).map(([valor, rotulo]) => ({ valor, rotulo })),
    },
    {
      nome: 'unidade',
      rotulo: 'Unidade',
      tipo: 'escolha',
      ajuda: 'Combustível e ARLA se medem em litro. Graxa, em quilo.',
      opcoes: [
        { valor: 'L', rotulo: 'Litro (L)' },
        { valor: 'kg', rotulo: 'Quilo (kg)' },
        { valor: 'un', rotulo: 'Unidade (un)' },
      ],
    },
  ],
  postos: [
    { nome: 'codigo', rotulo: 'Código', tipo: 'sistema' },
    { nome: 'nome', rotulo: 'Nome', tipo: 'texto', placeholder: 'Posto da rodovia', largo: true },
    /* O FORNECEDOR DO POSTO (pedido do dono, 31/10/2026): a pessoa de posto
       parceiro de um fornecedor so ve e so lanca nos postos dele (0123). Sem
       o fornecedor, o posto nao aparece para ninguem de posto parceiro. */
    {
      nome: 'fornecedorId',
      rotulo: 'Fornecedor',
      tipo: 'fornecedor',
      opcional: true,
      ajuda: 'A empresa dona do posto. Quem tem acesso de posto parceiro deste fornecedor lança neste posto, e só nele.',
    },
    {
      nome: 'cep',
      rotulo: 'CEP',
      tipo: 'cep',
      opcional: true,
      ajuda: 'Com o CEP, o endereço vem preenchido. Confira antes de gravar.',
    },
    { nome: 'logradouro', rotulo: 'Logradouro', tipo: 'texto', opcional: true, largo: true },
    { nome: 'numero', rotulo: 'Número', tipo: 'texto', opcional: true },
    { nome: 'bairro', rotulo: 'Bairro', tipo: 'texto', opcional: true },
    { nome: 'uf', rotulo: 'UF', tipo: 'uf', opcional: true },
    { nome: 'municipio', rotulo: 'Município', tipo: 'municipio', opcional: true },
  ],
  tanques: [
    { nome: 'codigo', rotulo: 'Código', tipo: 'sistema' },
    { nome: 'nome', rotulo: 'Nome', tipo: 'texto', placeholder: 'Tanque do pátio', largo: true },
    {
      nome: 'tipo',
      rotulo: 'Tipo',
      tipo: 'escolha',
      ajuda: 'Comboio é o tanque que se desloca até a máquina.',
      opcoes: [
        { valor: 'fixo', rotulo: 'Fixo' },
        { valor: 'comboio', rotulo: 'Comboio' },
      ],
    },
    { nome: 'produtoId', rotulo: 'Produto', tipo: 'referencia', ajuda: 'O que este tanque guarda.' },
    { nome: 'capacidadeL', rotulo: 'Capacidade (L)', tipo: 'numero', placeholder: '15.000' },
    {
      nome: 'toleranciaL',
      rotulo: 'Tolerância (L)',
      tipo: 'numero',
      opcional: true,
      raro: true,
      ajuda: 'Vale a MAIOR entre esta e a percentual. A absoluta protege o tanque pequeno.',
    },
    {
      nome: 'toleranciaPct',
      rotulo: 'Tolerância (%)',
      tipo: 'numero',
      opcional: true,
      raro: true,
      ajuda: 'Comboio em estrada de terra não mede como tanque fixo de pátio.',
    },
  ],
};

/** Os campos numéricos, que a tela converte antes de enviar. */
const NUMERICOS = new Set(['capacidadeL', 'toleranciaL', 'toleranciaPct']);

/**
 * O número guardado, escrito como se escreve no Brasil (p.12): ponto de
 * milhar, vírgula decimal — "15.000" e "0,5", e não "15000" e "0.5". Sem
 * arredondar: o que a edição mostra é o que ela grava de volta se ninguém
 * mexer, e é `interpretarNumero` que lê as duas formas.
 */
const NUMERO_DO_CAMPO = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 6 });

export function PaginaCadastrosDoCombustivel() {
  const base = useId();
  const [assunto, setAssunto] = useState<Assunto>('tanques');
  /* `versao`: a da linha QUANDO O FORMULARIO ABRIU (robos de 20/10/2026) — o
     servidor recusa o formulario velho em vez de gravar por cima. */
  const [emEdicao, setEmEdicao] = useState<{ readonly id: string | null; readonly versao?: string | null } | null>(null);
  const [valores, setValores] = useState<Record<string, string>>({});
  /* O que o CEP escreveu, ate a pessoa mexer (decisao 12: e sugestao). */
  const [preenchidos, setPreenchidos] = useState<ReadonlySet<string>>(new Set());
  /* "Mais dados" do tanque: as tolerâncias, que quase ninguém muda. */
  const [comMaisDados, setComMaisDados] = useState(false);
  /* Os campos que a PESSOA escolheu: a sugestão do sistema não passa por cima. */
  const [tocados, setTocados] = useState<ReadonlySet<string>>(new Set());
  const [erros, setErros] = useState<ErrosDeCampo>(NENHUM_ERRO);
  const [recusa, setRecusa] = useState<{ erro: string; comoResolver: string } | null>(null);

  const tanques = useTanquesCadastrados();
  const postos = usePostosCadastrados();
  const produtos = useProdutosCadastrados();
  /* Os fornecedores, para o posto (os mesmos da lista do lancamento). */
  const cadastrosDoLancamento = useCadastros();
  const gravar = useGravarCadastro(assunto);
  /* Cadastrar e editar tanque, posto e produto: só com `combustivel.gerir`
     (prova do marco, 07/10/2026). Quem só lê vê a lista, sem o botão. */
  const sessao = useSessao();
  const podeGerir = (sessao.data?.permissoes ?? []).includes('combustivel.gerir');

  const consulta = { tanques, postos, produtos }[assunto];
  const nome = NOME[assunto];

  /* O tanque exige um produto, e produto é outra aba desta mesma tela. Quando
     não há nenhum, o formulário não tem o que oferecer — e um `select` vazio é
     um beco: a pessoa preenche tudo e o botão não funciona. Melhor dizer antes,
     e levar até lá. */
  const produtosDisponiveis = useMemo(
    () => (produtos.data ?? []).filter((p) => p.ativo),
    [produtos.data],
  );
  const faltaProduto = assunto === 'tanques' && produtosDisponiveis.length === 0;

  function fecharFormulario() {
    setEmEdicao(null);
    setValores({});
    setComMaisDados(false);
    setTocados(new Set());
    setPreenchidos(new Set());
    setErros(NENHUM_ERRO);
    setRecusa(null);
  }

  function abrirFormulario(linha: TanqueCadastradoDaApi | PostoCadastradoDaApi | ProdutoCadastradoDaApi | null) {
    setRecusa(null);
    setErros(NENHUM_ERRO);
    setTocados(new Set());
    setComMaisDados(false);
    setPreenchidos(new Set());
    if (linha === null) {
      setEmEdicao({ id: null });
      /* Toda escolha nasce com a primeira opção preenchida: `select` sem valor
         manda string vazia, que o servidor recusa com uma frase que a pessoa
         não causou. */
      const iniciais: Record<string, string> = {};
      for (const campo of CAMPOS[assunto]) {
        if (campo.tipo === 'escolha') iniciais[campo.nome] = campo.opcoes?.[0]?.valor ?? '';
        if (campo.nome === 'produtoId') iniciais[campo.nome] = produtosDisponiveis[0]?.id ?? '';
      }
      setValores(iniciais);
      /* O produto do tanque novo é o primeiro da lista, e isso é o sistema
         escolhendo: a marca diz isso, para quem cadastra o tanque de ARLA
         conferir que não ficou com o diesel. */
      setPreenchidos(assunto === 'tanques' && iniciais['produtoId'] ? new Set(['produtoId']) : new Set());
      return;
    }

    const atual: Record<string, string> = {};
    for (const campo of CAMPOS[assunto]) {
      const valor = (linha as unknown as Record<string, unknown>)[campo.nome];
      atual[campo.nome] =
        valor === null || valor === undefined
          ? ''
          : NUMERICOS.has(campo.nome) && typeof valor === 'number'
            ? NUMERO_DO_CAMPO.format(valor)
            : String(valor);
    }
    const versao = (linha as unknown as Record<string, unknown>)['versao'];
    setEmEdicao({ id: linha.id, versao: typeof versao === 'string' ? versao : null });
    /* As caixas não estão em CAMPOS: sem esta linha, o arquivado abria como
       "Disponível" e o tanque padrão, desmarcado. */
    setValores({ ...atual, ...caixasDaLinha(linha, assunto === 'tanques') });
  }

  /* A mesma regra no `onBlur` e no envio (p.16): o erro aparece debaixo do
     campo ao sair dele, e o envio mostra todos de uma vez. */
  function mensagemDe(nome: string): string | null {
    const campo = CAMPOS[assunto].find((c) => c.nome === nome);
    if (campo === undefined || campo.tipo === 'sistema') return null;
    const cru = (valores[campo.nome] ?? '').trim();
    if (cru === '') return campo.opcional ? null : `Informe ${campo.rotulo.toLowerCase()}.`;
    /* A vírgula é como se escreve decimal em português, e o campo aceita as
       duas — e o ponto de milhar, que é como a edição mostra o número. */
    if (NUMERICOS.has(campo.nome) && interpretarNumero(cru) === null) {
      return 'Escreva só números, com vírgula para o decimal.';
    }
    return null;
  }

  function conferir(nome: string) {
    setErros((atual) => comErroDe(atual, nome, mensagemDe(nome)));
  }

  function aoEnviar(evento: FormEvent) {
    evento.preventDefault();
    if (emEdicao === null) return;

    const conferidos = conferirTodos(
      CAMPOS[assunto].map((campo) => campo.nome),
      mensagemDe,
    );
    setErros(conferidos);
    if (temErro(conferidos)) return;

    const envio: Record<string, unknown> = {};
    for (const campo of CAMPOS[assunto]) {
      /* O codigo e do sistema (0131): a API recusa o corpo que o traz. */
      if (campo.tipo === 'sistema') continue;
      const cru = (valores[campo.nome] ?? '').trim();
      /* Tirar o fornecedor de um posto e gravar `null`: pular o campo vazio
         deixaria o antigo no lugar. */
      if (cru === '' && campo.tipo === 'fornecedor' && emEdicao.id !== null) {
        envio[campo.nome] = null;
        continue;
      }
      if (cru === '') continue;
      envio[campo.nome] = NUMERICOS.has(campo.nome) ? interpretarNumero(cru) : cru;
    }
    /* E vão no envio, como booleano: sem elas, marcar e desmarcar não gravava nada. */
    Object.assign(envio, caixasDoEnvio(valores, { temPadrao: assunto === 'tanques', editando: emEdicao.id !== null }));

    setRecusa(null);
    gravar.mutate(
      emEdicao.id === null ? { envio } : { id: emEdicao.id, envio: comVersao(envio, emEdicao.versao) },
      {
        onSuccess: () => {
          fecharFormulario();
        },
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  const raros = CAMPOS[assunto].filter((campo) => campo.raro === true);
  const maisDadosAbertos =
    comMaisDados || raros.some((campo) => Boolean(erros[campo.nome]) || (valores[campo.nome] ?? '').trim() !== '');

  function mudarValor(campo: CampoDoFormulario, novo: string) {
    /* A UNIDADE SEGUE A CATEGORIA (`produto-pelo-nome.ts`), enquanto a pessoa
       nao escolheu a unidade a mao. */
    const unidade =
      assunto === 'produtos' && campo.nome === 'categoria' && !tocados.has('unidade')
        ? unidadePelaCategoria(novo, valores['nome'] ?? '')
        : null;
    /* Trocar a UF limpa o municipio: "Ipatinga" com UF "SP" e um
       endereco que nao existe. */
    setValores((antes) => ({
      ...antes,
      ...(campo.tipo === 'uf' ? { uf: novo, municipio: '' } : { [campo.nome]: novo }),
      ...(unidade ? { unidade } : {}),
    }));
    setErros((atual) => comErroDe(atual, campo.nome, null));
    setTocados((atual) => new Set([...atual, campo.nome]));
    setPreenchidos((atual) => {
      const sem = new Set([...atual].filter((nome) => nome !== campo.nome));
      if (unidade) sem.add('unidade');
      return sem;
    });
  }

  /* O NOME SUGERE A CATEGORIA, e a categoria a unidade: "ARLA 32" ja e ARLA,
     em litro. Ao sair do nome, no produto novo, e so enquanto a categoria nao
     foi escolhida a mao (`produto-pelo-nome.ts`). */
  function sugerirPeloNome(nome: string) {
    if (assunto !== 'produtos' || emEdicao?.id || tocados.has('categoria')) return;
    const categoria = categoriaPeloNome(nome);
    if (categoria === null) return;
    const unidade = tocados.has('unidade') ? null : unidadePelaCategoria(categoria, nome);
    setValores((antes) => ({ ...antes, categoria, ...(unidade ? { unidade } : {}) }));
    setPreenchidos((atual) => new Set([...atual, 'categoria', ...(unidade ? ['unidade'] : [])]));
  }

  const aoPreencherPeloCep = (achado: EnderecoDoCep) => {
    setValores((antes) => ({ ...antes, ...achado }));
    /* O CEP foi a pessoa que digitou; o resto, o provedor escreveu. */
    setPreenchidos(new Set(Object.keys(achado).filter((nome) => nome !== 'cep')));
  };

  /** Um campo da tabela, no lugar dele da grade. O endereco entra de uma vez, no CEP. */
  function desenhar(campo: CampoDoFormulario) {
    const id = `${base}-${campo.nome}`;
    const valor = valores[campo.nome] ?? '';
    const aoMudar = (novo: string) => {
      mudarValor(campo, novo);
    };
    const aoSair = () => {
      conferir(campo.nome);
      if (campo.nome === 'nome') sugerirPeloNome(valor);
    };

    /* O ENDERECO EM DUAS CAMADAS (`CamposDoEndereco`): CEP e numero a vista,
       o resto numa etiqueta que abre em "Ajustar endereco". O CEP fica
       aqui, por extenso — e ele que preenche o resto. */
    if (ehCampoDoEndereco(campo.nome) && campo.tipo !== 'cep') return null;
    if (campo.tipo === 'cep') {
      return (
        <Fragment key="endereco">
          <div className="min-w-0">
            <Campo
              id={id}
              rotulo={campo.rotulo}
              opcional
              {...(campo.ajuda === undefined || valor !== '' ? {} : { ajuda: campo.ajuda })}
              erro={erros[campo.nome] ?? null}
            >
              <CampoDeCep
                id={id}
                valor={valor}
                aoMudar={aoMudar}
                aoSair={aoSair}
                aoPreencher={aoPreencherPeloCep}
                {...marcaDeErro(erros, campo.nome, id)}
              />
            </Campo>
          </div>
          <CamposDoEndereco
            base={base}
            valores={enderecoDe(valores)}
            preenchidos={preenchidos}
            erros={erros}
            aoMudar={(nome, novo) => {
              const doEndereco = CAMPOS[assunto].find((c) => c.nome === nome);
              if (doEndereco) mudarValor(doEndereco, novo);
            }}
            aoSair={(nome) => {
              conferir(nome);
            }}
          />
        </Fragment>
      );
    }

    const opcoes =
      campo.tipo === 'fornecedor'
        ? (cadastrosDoLancamento.data?.fornecedores ?? []).map((f) => ({ valor: f.id, rotulo: f.nome }))
        : campo.tipo === 'referencia'
        ? produtosDisponiveis.map((p) => ({
            valor: p.id,
            rotulo: p.nome,
            etiquetas: [{ texto: CATEGORIA[p.categoria] ?? p.categoria, tom: 'neutro' as const, titulo: 'Categoria' }],
          }))
        : (campo.opcoes ?? []);

    /* 0131 · O CODIGO DO SISTEMA: so para ler. Cadastrando, ele ainda
       nao existe, e a linha diz quando ele nasce. */
    if (campo.tipo === 'sistema') {
      return (
        <div key={campo.nome} className="flex min-w-0 flex-col gap-2">
          <span className="txt-rotulo-campo">{campo.rotulo}</span>
          {valor === '' ? (
            <span className="txt-apoio text-tinta-secundaria">O código é criado pelo sistema ao salvar.</span>
          ) : (
            <span className="codigo text-tinta">{valor}</span>
          )}
        </div>
      );
    }

    const preenchido = preenchidos.has(campo.nome);
    const ajuda =
      preenchido && campo.nome === 'produtoId'
        ? 'O primeiro da lista. Confira se é o que este tanque guarda.'
        : preenchido && (campo.nome === 'categoria' || campo.nome === 'unidade')
          ? campo.nome === 'categoria'
            ? 'Veio do nome. Troque se não for.'
            : 'Veio da categoria. Troque se não for.'
          : campo.ajuda;

    return (
      <div key={campo.nome} className={campo.largo === true ? 'campo-largo min-w-0' : 'min-w-0'}>
        <Campo
          id={id}
          rotulo={campo.rotulo}
          {...(campo.opcional === true ? { opcional: true } : {})}
          {...(ajuda === undefined ? {} : { ajuda })}
          preenchido={preenchido}
          erro={erros[campo.nome] ?? null}
        >
          {campo.tipo === 'referencia' && produtos.isPending ? (
            /* O tanque aberto pela linha antes de os produtos chegarem:
               o controle é esqueleto, e não uma lista vazia. */
            <ControleCarregando oQue="Carregando os produtos" />
          ) : campo.tipo === 'fornecedor' && cadastrosDoLancamento.isPending ? (
            <ControleCarregando oQue="Carregando os fornecedores" />
          ) : campo.tipo === 'escolha' || campo.tipo === 'referencia' || campo.tipo === 'fornecedor' ? (
            /* O seletor do sistema (p.23, p.31), e nao o `<select>` nativo:
               o produto com a categoria em etiqueta, e o obrigatorio com uma
               opcao so ja vem escolhido (`lib/escolha-unica.ts`). */
            <ListaDeEscolha
              id={id}
              rotulo={campo.rotulo}
              valor={valor}
              aoMudar={aoMudar}
              aoSair={aoSair}
              {...(campo.tipo === 'referencia' ? { rotuloVazio: 'Escolha o produto' } : {})}
              {...(campo.tipo === 'fornecedor' ? { rotuloVazio: 'Sem fornecedor' } : {})}
              opcoes={opcoes}
              idDoErro={erros[campo.nome] ? `${id}-erro` : undefined}
            />
          ) : (
            <input
              id={id}
              type="text"
              autoComplete="off"
              className="controle w-full"
              value={valor}
              {...(campo.tipo === 'numero' ? { inputMode: 'decimal' as const } : {})}
              {...(campo.placeholder === undefined ? {} : { placeholder: campo.placeholder })}
              onChange={(evento) => {
                aoMudar(evento.target.value);
              }}
              onBlur={aoSair}
              {...marcaDeErro(erros, campo.nome, id)}
            />
          )}
        </Campo>
      </div>
    );
  }

  const colunas = colunasDe(assunto) as readonly Coluna<Record<string, unknown>>[];
  const linhas = (consulta.data ?? []) as unknown as readonly Record<string, unknown>[];
  const paginaDoCadastro = usePaginacao(linhas);

  /* A faixa da R01 (p.29) sai da CONTAGEM de campos da propria tabela, e nao
     de uma lista a mao: ate 6 no modal (centro), 7 a 10 na gaveta (direita).
     Assim um campo novo no tanque nao deixa o formulario no lugar errado — foi
     exatamente assim que ele passou do limite do modal sem ninguem notar. */
  const lugarDoFormulario = CAMPOS[assunto].length > 6 ? 'direita' : 'centro';

  return (
    /* A MOLDURA E O HUB DO COMBUSTIVEL, a mesma das outras abas: titulo,
       indicadores e abas vem dele, e esta tela e so o conteudo da aba. */
    <HubDoCombustivel>
      {/* A ação de cadastrar é SECUNDÁRIA aqui: o laranja da tela é o
          "Lançar abastecimento" do hub. O formulário abre em janela, e lá
          dentro o "Gravar" é o laranja — o único que se alcança com ela
          aberta. */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <Seletor rotulo="Cadastro" valor={assunto} opcoes={ASSUNTOS} aoEscolher={(novo) => {
          setAssunto(novo);
          fecharFormulario();
        }} />
        {podeGerir ? (
          <Botao
            nivel="secundario"
            disabled={faltaProduto}
            onClick={() => {
              abrirFormulario(null);
            }}
          >
            {`Cadastrar ${nome.um}`}
          </Botao>
        ) : null}
      </div>

      {/* Só depois da resposta: antes dela, "nenhum produto foi cadastrado"
          afirmava o que ninguém sabia ainda (p.32). */}
      {podeGerir && faltaProduto && !produtos.isPending ? (
        <p className="txt-apoio text-tinta-secundaria">
          Um tanque guarda um produto, e nenhum produto foi cadastrado ainda. Cadastre o produto primeiro (diesel, ARLA
          ou lubrificante), e o tanque passa a ter o que guardar.
        </p>
      ) : null}

      <Tabela
          rotulo={`${nome.muitos} cadastrados`}
          colunas={colunas}
          linhas={paginaDoCadastro.visiveis}
          rodape={<RodapeTabela {...paginaDoCadastro.rodape} />}
          chaveDaLinha={(linha) => String(linha['id'])}
          carregando={consulta.isPending}
          {...(podeGerir
            ? {
                aoSelecionar: (linha: Record<string, unknown>) => {
                  abrirFormulario(linha as unknown as TanqueCadastradoDaApi);
                },
              }
            : {})}
          /* A ACAO ESCRITA NA LINHA (p.18). A linha ja abria o formulario ao
             ser clicada, e isso e invisivel: quem nao adivinha nunca descobre
             que da para editar. O clique continua funcionando; o botao e o que
             torna a acao ALCANCAVEL, inclusive pelo teclado. */
          {...(podeGerir
            ? {
                acoes: (linha: Record<string, unknown>) => (
                  <Botao
                    nivel="terciario"
                    onClick={() => {
                      abrirFormulario(linha as unknown as TanqueCadastradoDaApi);
                    }}
                  >
                    Editar
                  </Botao>
                ),
              }
            : {})}
          erro={
            consulta.isError ? (
              <EstadoVazio
                titulo={`Não foi possível carregar ${nome.muitos.toLowerCase()}`}
                descricao={explicarFalha(consulta.error).comoResolver}
                acao={
                  <Botao
                    nivel="secundario"
                    onClick={() => {
                      void consulta.refetch();
                    }}
                  >
                    Carregar outra vez
                  </Botao>
                }
              />
            ) : null
          }
          vazio={
            <EstadoVazio
              titulo={`Nenhum ${nome.um} cadastrado`}
              descricao={
                !podeGerir
                  ? `Quem cadastra ${nome.um} é o perfil Administrador. Peça a ele, e o cadastro passa a aparecer aqui.`
                  : assunto === 'tanques'
                    ? 'Cadastre o tanque do pátio ou o comboio. Ele passa a aparecer no formulário de lançamento, e o marcado como padrão já vem escolhido.'
                    : `Cadastre o primeiro ${nome.um}. Ele passa a aparecer nas listas de escolha das outras telas.`
              }
              acao={
                podeGerir ? (
                  <Botao
                    nivel="secundario"
                    disabled={faltaProduto}
                    onClick={() => {
                      abrirFormulario(null);
                    }}
                  >
                    {`Cadastrar ${nome.um}`}
                  </Botao>
                ) : undefined
              }
            />
          }
        />

        {/* O LUGAR VEM DA CONTAGEM DE CAMPOS (R01, p.29), e nao do gosto:
            modal ate 6 campos, gaveta de 400-560 px para o formulario medio
            sem sair da lista. Tanque tem SETE — um acima do limite do modal —,
            e era por isso que ele abria com 740 px de altura numa caixa de 85%
            da tela. Posto e produto tem quatro, e continuam no modal.
            "Cancelar" terciário à esquerda (p.15), no RODAPÉ FIXO (R01, p.29):
            fora do corpo que rola, ligado ao formulário pelo atributo `form`.
            E o primário diz O QUE grava — verbo + objeto; "Gravar" sozinho é
            da família de "OK" e "Enviar". */}
        <Sobreposicao
          lugar={lugarDoFormulario}
          titulo={emEdicao?.id ? `Editar ${nome.um}` : `Cadastrar ${nome.um}`}
          aberta={emEdicao !== null && podeGerir}
          aoFechar={fecharFormulario}
          acoes={
            <>
              <Botao nivel="terciario" type="button" onClick={fecharFormulario}>
                Cancelar
              </Botao>
              <Botao nivel="primario" type="submit" form={`${base}-formulario`} disabled={gravar.isPending}>
                {gravar.isPending ? 'Gravando…' : `Gravar ${nome.um}`}
              </Botao>
            </>
          }
        >
          {emEdicao === null || !podeGerir ? null : (
          /* Grade da R01 (p.28). Ela dá duas colunas nas duas sobreposições
             sem nada escrito aqui: `formulario-grade` é um container query, e
             tanto a gaveta (400-560) quanto o modal (480-640) são estreitos
             demais para a terceira coluna de 240 px caber. */
          <form id={`${base}-formulario`} onSubmit={aoEnviar} className="formulario-grade">
            {/* A recusa no TOPO do formulário (p.22). */}
            {recusa ? (
              <div role="alert" className="campo-linha flex flex-col gap-1">
                <span className="txt-corpo text-parada">{recusa.erro}</span>
                <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
              </div>
            ) : null}
            {CAMPOS[assunto].filter((campo) => campo.raro !== true).map(desenhar)}

            {/* O RARO atrás de "Mais dados" (31/10/2026): as tolerâncias do
                tanque. Com erro ou com valor gravado, a camada abre sozinha:
                o que está escrito não fica escondido. */}
            {CAMPOS[assunto].some((campo) => campo.raro === true) ? (
              <>
                <BotaoDoRaro
                  aberto={maisDadosAbertos}
                  aoAlternar={() => {
                    setComMaisDados((aberto) => !aberto);
                  }}
                  rotulo="Mais dados"
                  rotuloAberto="Fechar mais dados"
                  idDoConteudo={`${base}-mais-dados`}
                />
                {maisDadosAbertos ? (
                  <div id={`${base}-mais-dados`} className="campo-linha grade-do-painel bloco-rebaixado p-4">
                    {CAMPOS[assunto].filter((campo) => campo.raro === true).map(desenhar)}
                  </div>
                ) : null}
              </>
            ) : null}

            {assunto === 'tanques' ? (
              <label className="campo-linha flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={valores['padrao'] === 'true'}
                  onChange={(evento) => {
                    setValores((antes) => ({ ...antes, padrao: String(evento.target.checked) }));
                  }}
                />
                <span className="txt-apoio text-tinta-secundaria">
                  Este é o tanque padrão: o que já vem escolhido no formulário de lançamento. Só um por empresa.
                </span>
              </label>
            ) : null}

            {emEdicao.id === null ? null : (
              <label className="campo-linha flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={valores['ativo'] !== 'false'}
                  onChange={(evento) => {
                    setValores((antes) => ({ ...antes, ativo: String(evento.target.checked) }));
                  }}
                />
                <span className="txt-apoio text-tinta-secundaria">
                  Disponível. Desmarcar arquiva o cadastro: ele some das listas de escolha e continua explicando os
                  lançamentos que já aconteceram.
                </span>
              </label>
            )}

          </form>
          )}
        </Sobreposicao>
    </HubDoCombustivel>
  );
}

/* ==========================================================================
 * As colunas de cada lista
 * ======================================================================= */

function colunasDe(assunto: Assunto): readonly Coluna<never>[] {
  if (assunto === 'produtos') {
    const colunas: readonly Coluna<ProdutoCadastradoDaApi>[] = [
      { chave: 'codigo', rotulo: 'Código', celula: (l) => <Codigo valor={l.codigo} /> },
      { chave: 'nome', rotulo: 'Nome', celula: (l) => l.nome },
      { chave: 'categoria', rotulo: 'Categoria', celula: (l) => CATEGORIA[l.categoria] ?? l.categoria },
      { chave: 'unidade', rotulo: 'Unidade', celula: (l) => l.unidade },
      { chave: 'ativo', rotulo: 'Situação', alinhamento: 'status', celula: (l) => <Situacao ativo={l.ativo} /> },
    ];
    return colunas as unknown as readonly Coluna<never>[];
  }

  if (assunto === 'postos') {
    const colunas: readonly Coluna<PostoCadastradoDaApi>[] = [
      { chave: 'codigo', rotulo: 'Código', celula: (l) => <Codigo valor={l.codigo} /> },
      { chave: 'nome', rotulo: 'Nome', celula: (l) => l.nome },
      { chave: 'municipio', rotulo: 'Município', celula: (l) => [l.municipio, l.uf].filter(Boolean).join(' · ') || '—' },
      { chave: 'fornecedor', rotulo: 'Fornecedor', celula: (l) => l.fornecedor ?? '—' },
      { chave: 'ativo', rotulo: 'Situação', alinhamento: 'status', celula: (l) => <Situacao ativo={l.ativo} /> },
    ];
    return colunas as unknown as readonly Coluna<never>[];
  }

  const colunas: readonly Coluna<TanqueCadastradoDaApi>[] = [
    { chave: 'codigo', rotulo: 'Código', celula: (l) => <Codigo valor={l.codigo} /> },
    {
      chave: 'nome',
      rotulo: 'Nome',
      /* `flex-wrap`: no cartão de meia largura (390 px) o marcador desce para
         baixo do nome, em vez de espremer "Tanque do pátio" em duas linhas
         para caber ao lado dele. Na tabela, com espaço, os dois seguem juntos. */
      celula: (l) => (
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span>{l.nome}</span>
          {l.padrao ? <span className="txt-selo inline-flex h-[var(--selo-altura)] items-center rounded-etiqueta bg-tela px-2 text-tinta-lista">Padrão</span> : null}
        </span>
      ),
    },
    { chave: 'tipo', rotulo: 'Tipo', celula: (l) => TIPO_DE_TANQUE[l.tipo] ?? l.tipo },
    { chave: 'produto', rotulo: 'Produto', celula: (l) => l.produto },
    {
      chave: 'capacidadeL',
      rotulo: 'Capacidade',
      alinhamento: 'numero',
      /* `Medida`: a unidade em cinza e sem negrito (p.12). */
      celula: (l) => <Medida valor={formatarInteiro(l.capacidadeL)} unidade="L" />,
    },
    { chave: 'obra', rotulo: 'Obra', celula: (l) => l.obra ?? '—' },
    { chave: 'ativo', rotulo: 'Situação', alinhamento: 'status', celula: (l) => <Situacao ativo={l.ativo} /> },
  ];
  return colunas as unknown as readonly Coluna<never>[];
}
